import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Button,
  Table,
  message,
  Divider,
  Row,
  Col,
  Card,
  AutoComplete
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  SendOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import vendorBillService, { VendorBill, VendorBillItem, CreateVendorBillInput } from '../../../../services/vendorBillService';
import apiService from '../../../../services/apiService';
import hrService, { Department } from '../../../../services/hrService';

const { Option } = Select;
const { TextArea } = Input;

interface VendorBillFormProps {
  visible: boolean;
  bill?: VendorBill | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface LineItem extends Omit<VendorBillItem, 'id' | 'billId' | 'createdAt'> {
  key: string;
}

const VendorBillForm: React.FC<VendorBillFormProps> = ({ visible, bill, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [expenseAccounts, setExpenseAccounts] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [journals, setJournals] = useState<any[]>([]);
  const [accountingSettings, setAccountingSettings] = useState<any>(null);
  const [departments, setDepartments] = useState<Department[]>([]);

  // Departments are still fetched, but ONLY to render a line saved before this
  // field was unlinked from HR. They are never offered as choices.
  const legacyDepartmentName = (departmentId?: string | null): string => {
    if (!departmentId) return '';
    const d = departments.find(x => x.id === departmentId);
    return d ? `${d.code} - ${d.name}` : '';
  };
  const [customers, setCustomers] = useState<any[]>([]);
  const [subtotal, setSubtotal] = useState(0);
  const [totalTax, setTotalTax] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    if (visible) {
      loadVendors();
      loadAccounts();
      loadJournals();
      loadAccountingSettings();
      loadDepartments();
      loadCustomers();
      if (bill) {
        // Edit mode
        form.setFieldsValue({
          billNo: bill.billNo,
          vendorId: bill.vendorId,
          billDate: bill.billDate ? dayjs(bill.billDate) : null,
          dueDate: bill.dueDate ? dayjs(bill.dueDate) : null,
          accountingDate: bill.accountingDate ? dayjs(bill.accountingDate) : null,
          chequeNo: bill.chequeNo || undefined,
          invoiceNo: bill.invoiceNo || undefined,
          description: (bill as any).description || undefined
        });
        const items: LineItem[] = bill.items.map((item, index) => ({
          key: `item-${index}`,
          description: item.description || '',
          productId: item.productId,
          quantity: Number(item.quantity || 0),
          unitPrice: Number(item.unitPrice || 0),
          discount: Number(item.discount || 0),
          accountId: item.accountId,
          departmentId: item.departmentId || undefined,
          departmentName: (item as any).departmentName ?? undefined,
          partnerId: (item as any).partnerId || undefined,
          subtotal: Number(item.subtotal || 0),
          taxIds: item.taxIds || []
        }));
        setLineItems(items);
        calculateTotal(items);
      } else {
        // Create mode
        form.resetFields();
        setLineItems([]);
        setSubtotal(0);
        setTotalTax(0);
        setTotalAmount(0);
      }
    }
  }, [visible, bill, form]);

  const loadVendors = async () => {
    try {
      const response: any = await apiService.get('/accounting/vendors');
      setVendors(response.vendors || response.data || []);
    } catch (error) {
      console.error('Failed to load vendors:', error);
      message.error('Failed to load vendors');
    }
  };

  const loadAccounts = async () => {
    try {
      const response: any = await apiService.get('/accounting/accounts');
      const allAccounts = response.data || [];
      setAccounts(allAccounts);

      // Filter expense accounts (type EXPENSE)
      const expenseAccts = allAccounts.filter((acc: any) => acc.accountType === 'EXPENSE');
      setExpenseAccounts(expenseAccts);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    }
  };

  const loadJournals = async () => {
    try {
      const response: any = await apiService.get('/accounting/journals/active');
      setJournals(response.data || []);
    } catch (error) {
      console.error('Failed to load journals:', error);
    }
  };

  const loadAccountingSettings = async () => {
    try {
      const response: any = await apiService.get('/accounting/settings');
      setAccountingSettings(response.data || null);
    } catch (error) {
      // Settings are optional; resolution falls back to code-based heuristics.
      console.error('Failed to load accounting settings:', error);
    }
  };

  const loadDepartments = async () => {
    try {
      const list = await hrService.department.getAll();
      setDepartments(list);
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const res: any = await apiService.get('/customers/dropdown');
      setCustomers(res?.data || res?.customers || []);
    } catch (error) {
      // Optional: the Partner field accepts free text if the list is unavailable.
      console.error('Failed to load customers:', error);
    }
  };

  // Options for the per-line Partner box. Stored as the plain name, matching
  // how journal entry lines hold it, so the GL and exports render it directly.
  const partnerOptions = React.useMemo(() => ([
    ...vendors.map((v: any) => ({ value: v.name, label: `${v.name} · Vendor` })),
    ...customers.map((c: any) => ({ value: c.name, label: `${c.name} · Customer` }))
  ]), [vendors, customers]);

  const addLineItem = () => {
    const newItem: LineItem = {
      key: `item-${Date.now()}`,
      description: '',
      quantity: 1,
      unitPrice: 0,
      discount: 0,
      accountId: expenseAccounts[0]?.id || '',
      departmentId: undefined,
      departmentName: undefined,
      partnerId: undefined,
      subtotal: 0,
      taxIds: []
    };
    const newItems = [...lineItems, newItem];
    setLineItems(newItems);
  };

  const updateLineItem = (key: string, field: keyof LineItem, value: any) => {
    const newItems = lineItems.map(item => {
      if (item.key === key) {
        const updatedItem = { ...item, [field]: value };
        // Recalculate subtotal
        if (field === 'quantity' || field === 'unitPrice' || field === 'discount') {
          const quantity = Number(updatedItem.quantity || 0);
          const unitPrice = Number(updatedItem.unitPrice || 0);
          const discount = Number(updatedItem.discount || 0);
          updatedItem.subtotal = (quantity * unitPrice) - discount;
        }
        return updatedItem;
      }
      return item;
    });
    setLineItems(newItems);
    calculateTotal(newItems);
  };

  const removeLineItem = (key: string) => {
    const newItems = lineItems.filter(item => item.key !== key);
    setLineItems(newItems);
    calculateTotal(newItems);
  };

  const calculateTotal = (items: LineItem[]) => {
    const sub = items.reduce((sum, item) => sum + Number(item.subtotal || 0), 0);
    setSubtotal(sub);
    setTotalTax(0); // For now, no tax calculation
    setTotalAmount(sub);
  };

  const handleSubmit = async (isDraft: boolean = true) => {
    try {
      const values = await form.validateFields();

      if (lineItems.length === 0) {
        message.error('Please add at least one line item');
        return;
      }

      const invalidItems = lineItems.filter(
        item => !item.description?.trim() || item.quantity <= 0 || item.unitPrice <= 0 || !item.accountId
      );
      if (invalidItems.length > 0) {
        message.error('Please enter valid data for all line items including expense account');
        return;
      }

      setLoading(true);

      const billData: CreateVendorBillInput = {
        // Blank → backend assigns the next {JournalCode}/{YYYY}/{MM}/{NNNN}.
        billNo: values.billNo?.trim() || undefined,
        vendorId: values.vendorId,
        billDate: values.billDate ? values.billDate.format('YYYY-MM-DD') : new Date().toISOString().split('T')[0],
        dueDate: values.dueDate ? values.dueDate.format('YYYY-MM-DD') : undefined,
        accountingDate: values.accountingDate ? values.accountingDate.format('YYYY-MM-DD') : undefined,
        amountUntaxed: subtotal,
        amountTax: totalTax,
        amountTotal: totalAmount,
        chequeNo: values.chequeNo?.trim() || undefined,
        invoiceNo: values.invoiceNo?.trim() || undefined,
        description: values.description?.trim() || undefined,
        items: lineItems.map(item => ({
          productId: item.productId,
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          discount: item.discount || 0,
          accountId: item.accountId,
          departmentId: item.departmentId || undefined,
          departmentName: (item as any).departmentName ?? undefined,
          partnerId: (item as any).partnerId || undefined,
          subtotal: item.subtotal,
          taxIds: item.taxIds || []
        })),
        taxes: []
      };

      if (bill) {
        // Update existing bill
        await vendorBillService.updateBill(bill.id, billData);
        message.success('Vendor bill updated successfully');
      } else {
        // Create new bill
        const newBill = await vendorBillService.createBill(billData);

        // If "Create & Post", post the bill to create journal entry
        if (!isDraft) {
          // Prefer Default Accounts from settings, fall back to code/type
          // heuristics so older databases still work. The expense account
          // here is just a fallback for line items missing accountId — the
          // backend uses `item.accountId || expenseAccountId`, so any line
          // item account works as the safety net.
          const apById = accountingSettings?.defaultPayableAccountId
            ? accounts.find(a => a.id === accountingSettings.defaultPayableAccountId)
            : null;
          const expenseById = accountingSettings?.defaultExpenseAccountId
            ? accounts.find(a => a.id === accountingSettings.defaultExpenseAccountId)
            : null;
          const apAccount = apById
            || accounts.find(a => a.code === '220000')
            || accounts.find(a => a.code === '210000')
            || accounts.find(a => a.code === '2100')
            || accounts.find(a => a.code === '2000')
            || accounts.find(a => a.code?.startsWith('2100'))
            || accounts.find(a => a.code?.startsWith('22'));
          const firstLineAccountId = lineItems.find(li => li.accountId)?.accountId;
          const expenseAccount = expenseById
            || (firstLineAccountId ? accounts.find(a => a.id === firstLineAccountId) : null)
            || expenseAccounts[0];
          const purchaseJournal = journals.find(j => j.code === 'PUR')
            || journals.find(j => j.code === 'BILL')
            || journals.find(j => j.type === 'PURCHASE');

          if (!apAccount || !expenseAccount || !purchaseJournal) {
            console.error('Vendor Bill post — account/journal resolution failed:', {
              apAccount: apAccount ? { id: apAccount.id, code: apAccount.code } : null,
              expenseAccount: expenseAccount ? { id: expenseAccount.id, code: expenseAccount.code } : null,
              purchaseJournal: purchaseJournal ? { id: purchaseJournal.id, code: purchaseJournal.code, type: purchaseJournal.type } : null,
              defaultPayableAccountId: accountingSettings?.defaultPayableAccountId,
              defaultExpenseAccountId: accountingSettings?.defaultExpenseAccountId,
              allAccountCodes: accounts.map(a => a.code),
              allJournalCodes: journals.map(j => `${j.code}(${j.type})`)
            });
            const missing: string[] = [];
            if (!apAccount) missing.push('Accounts Payable');
            if (!expenseAccount) missing.push('Expense account');
            if (!purchaseJournal) missing.push('Purchase Journal');
            message.error(
              `Cannot post: missing ${missing.join(', ')}. ` +
              `Set the defaults under Accounting → Settings, or create accounts (AP code 2100/2000) and a journal with code PUR / type PURCHASE.`
            );
            setLoading(false);
            return;
          }

          try {
            await vendorBillService.postBill(newBill.id, {
              accountsPayableAccountId: apAccount.id,
              expenseAccountId: expenseAccount.id,
              journalId: purchaseJournal.id
            });
            message.success('Vendor bill created and posted successfully');
          } catch (error: any) {
            message.error(`Vendor bill created but failed to post: ${error.message}`);
          }
        } else {
          message.success('Vendor bill saved as draft successfully');
        }
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      message.error(`Failed to save vendor bill: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const lineItemColumns: ColumnsType<LineItem> = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 165,
      render: (value, record) => (
        // Multi-line: press Enter for line breaks. autoSize keeps the row
        // height in sync with content (1-6 visible rows).
        <TextArea
          value={value}
          placeholder="Item description (Enter for new line)"
          onChange={(e) => updateLineItem(record.key, 'description', e.target.value)}
          autoSize={{ minRows: 1, maxRows: 6 }}
          style={{ width: '100%', resize: 'none' }}
        />
      )
    },
    {
      title: 'Account',
      dataIndex: 'accountId',
      key: 'accountId',
      width: 200,
      render: (value, record) => (
        <Select
          value={value}
          onChange={(val) => updateLineItem(record.key, 'accountId', val)}
          style={{ width: '100%' }}
          placeholder="Select account"
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) =>
            (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
          }
          // Show ALL accounts (not just EXPENSE-type) so users can post
          // bills against any account when needed (asset purchases,
          // adjustments, prepayments, etc.).
          options={accounts.map(account => ({
            value: account.id,
            label: `${account.code} - ${account.name}${account.accountType ? ` (${account.accountType})` : ''}`
          }))}
        />
      )
    },
    {
      title: 'Partner',
      dataIndex: 'partnerId',
      key: 'partnerId',
      width: 150,
      // Per-line counterparty, mirroring the journal entry form. Flows onto the
      // journal entry line on post, so it reaches the GL and the export.
      render: (value, record) => (
        <AutoComplete
          style={{ width: '100%' }}
          value={value || undefined}
          options={partnerOptions}
          placeholder="Customer / Vendor"
          allowClear
          // Company names are longer than the column, so let the suggestion
          // list size to its content instead of clipping every name.
          popupMatchSelectWidth={260}
          onChange={(val) => updateLineItem(record.key, 'partnerId', val)}
          filterOption={(input, option) =>
            (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
          }
        />
      )
    },
    {
      title: 'Department',
      dataIndex: 'departmentName',
      key: 'departmentName',
      width: 160,
      // Free text, not a picker — unlinked from the HR department list at the
      // client's request. Lines saved before the change show their old name.
      render: (_value, record) => (
        <Input
          value={(record as any).departmentName ?? legacyDepartmentName(record.departmentId)}
          onChange={(e) => updateLineItem(record.key, 'departmentName' as any, e.target.value)}
          style={{ width: '100%' }}
          placeholder="(Optional)"
        />
      )
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 85,
      render: (value, record) => (
        <InputNumber
          min={1}
          value={value}
          onChange={(val) => updateLineItem(record.key, 'quantity', val || 1)}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      // Was '13%'. Percentage widths on a table that also has px widths get
      // squeezed by Ant — this column collapsed to ~51px, too narrow to read a
      // price in. All columns on this table are px now, and they add up to the
      // modal's table width so nothing is squeezed and nothing scrolls.
      width: 170,
      render: (value, record) => (
        <InputNumber
          min={0}
          precision={2}
          prefix="$"
          value={value}
          onChange={(val) => updateLineItem(record.key, 'unitPrice', val || 0)}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: 'Discount',
      dataIndex: 'discount',
      key: 'discount',
      width: 100,
      render: (value, record) => (
        <InputNumber
          min={0}
          precision={2}
          prefix="$"
          value={value}
          onChange={(val) => updateLineItem(record.key, 'discount', val || 0)}
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: 'Subtotal',
      dataIndex: 'subtotal',
      key: 'subtotal',
      width: 105,
      align: 'right',
      render: (value) => (
        <span style={{ fontWeight: 500 }}>
          ${Number(value).toFixed(2)}
        </span>
      )
    },
    {
      title: '',
      key: 'action',
      width: 45,
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeLineItem(record.key)}
        />
      )
    }
  ];

  return (
    <Modal
      title={bill ? 'Edit Vendor Bill' : 'Create New Vendor Bill'}
      open={visible}
      onCancel={onClose}
      width={1200}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
      >
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="billNo"
              label="Entry # (Bill No.)"
              tooltip="Leave blank to auto-generate (BILL-#####), or type a custom number. Must be globally unique."
              rules={[
                { max: 64, message: 'Bill No. must be 64 characters or fewer' }
              ]}
            >
              <Input
                placeholder="(Auto-generated on save)"
                allowClear
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="vendorId"
              label="Vendor"
              rules={[{ required: true, message: 'Please select a vendor' }]}
            >
              <Select
                showSearch
                placeholder="Select a vendor"
                optionFilterProp="children"
                filterOption={(input, option) => {
                  const children = option?.children as any;
                  const searchText = Array.isArray(children)
                    ? children.join(' ')
                    : String(children);
                  return searchText.toLowerCase().includes(input.toLowerCase());
                }}
              >
                {vendors.map(vendor => (
                  <Option key={vendor.id} value={vendor.id}>
                    {vendor.name}
                    {vendor.code && ` (${vendor.code})`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="billDate"
              label="Bill Date"
              rules={[{ required: true, message: 'Please select bill date' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="dueDate"
              label="Due Date"
            >
              <DatePicker
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="accountingDate"
              label="Accounting Date"
              tooltip="The period in which this bill is booked. When set, drives the journal entry date (so GL filters under this period); otherwise falls back to Bill Date."
            >
              <DatePicker
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="chequeNo"
              label="Cheque No."
              tooltip="Cheque number used (or to be used) to pay this bill. Flows through to the journal entry's Cheque No. field."
            >
              <Input placeholder="Cheque number (if applicable)" allowClear />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="invoiceNo"
              label="Invoice No."
              tooltip="The supplier's printed invoice number. Separate from Bill No. (which is our internal reference). Flows through to the journal entry's Invoice No. field."
            >
              <Input placeholder="Supplier's invoice number" allowClear />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="description"
              label="Description"
              tooltip="What this bill is for. Becomes the journal entry's description. Leave blank to keep the generated text (Vendor Bill - <Bill No.> - <Vendor>)."
            >
              <Input.TextArea
                placeholder="What is this bill for? (used as the journal entry description)"
                autoSize={{ minRows: 2 }}
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider>Line Items</Divider>

        <Table
          columns={lineItemColumns}
          dataSource={lineItems}
          pagination={false}
          size="small"
          style={{ marginBottom: 16 }}
          scroll={{ x: 1150 }}
          tableLayout="fixed"
          footer={() => (
            <Button
              type="dashed"
              onClick={addLineItem}
              icon={<PlusOutlined />}
              block
            >
              Add Line Item
            </Button>
          )}
        />

        <Card size="small" style={{ marginBottom: 16 }}>
          <Row justify="end">
            <Col span={8}>
              <div style={{ textAlign: 'right' }}>
                <div>Subtotal: ${Number(subtotal || 0).toFixed(2)}</div>
                <div>Tax: ${Number(totalTax || 0).toFixed(2)}</div>
                <div style={{ fontSize: 16, fontWeight: 'bold', marginTop: 8 }}>
                  Total Amount: ${Number(totalAmount || 0).toFixed(2)}
                </div>
              </div>
            </Col>
          </Row>
        </Card>

        <Row justify="end" gutter={8}>
          <Col>
            <Button onClick={onClose}>
              Cancel
            </Button>
          </Col>
          {!bill && (
            <>
              <Col>
                <Button
                  onClick={() => handleSubmit(true)}
                  loading={loading}
                  icon={<SaveOutlined />}
                >
                  Save as Draft
                </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  onClick={() => handleSubmit(false)}
                  loading={loading}
                  icon={<SendOutlined />}
                >
                  Create & Post
                </Button>
              </Col>
            </>
          )}
          {bill && (
            <Col>
              <Button
                type="primary"
                onClick={() => handleSubmit(true)}
                loading={loading}
                icon={<SaveOutlined />}
              >
                Update Bill
              </Button>
            </Col>
          )}
        </Row>
      </Form>
    </Modal>
  );
};

export default VendorBillForm;
